//************************************************************************************************
// JpmNotificationRecoveryModal
//
// Self-contained Tier 1 action: lets support request recovery of a JPM notification for a
// selected transaction that is stuck on the JPM host (payHostCode 64) and has not reached a
// resolved status yet. Only used by SupportFiscalReport.jsx.
//************************************************************************************************

import React, { useState } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	makeStyles,
	TextField,
	Typography,
} from '@material-ui/core';
import { requestJpmNotification } from '../services/jpmNotificationService.js';

const useStyles = makeStyles(theme => ({
	reminder: {
		marginBottom: theme.spacing(1),
		color: theme.palette.text.secondary,
	},
	success: { color: theme.palette.success.main },
	warning: { color: theme.palette.warning.main },
	error: { color: theme.palette.error.main },
}));

export default function JpmNotificationRecoveryModal({ transaction, onClose }) {
	const classes = useStyles();
	const requestKey = transaction && `jpm-notification-requested:${transaction.subDept}:${transaction.txnID}`;
	const previouslySubmitted = requestKey && sessionStorage.getItem(requestKey) === '1';
	const [jpmDateTime, setJpmDateTime] = useState('');
	const [status, setStatus] = useState(previouslySubmitted ? 'JPM notification request already submitted. Local processing continues asynchronously.' : '');
	const [statusTone, setStatusTone] = useState(previouslySubmitted ? 'success' : '');
	const [submitting, setSubmitting] = useState(false);

	if (!transaction) {
		return null;
	}

	const handleSubmit = e => {
		e.preventDefault();
		if (submitting || statusTone === 'success') return;

		if (!jpmDateTime) {
			setStatus('Enter the JPM date/time before submitting.');
			setStatusTone('error');
			return;
		}

		const createdAt = new Date(jpmDateTime).toISOString();

		setSubmitting(true);
		setStatus('Pending...');
		setStatusTone('');

		requestJpmNotification({ subDept: transaction.subDept, txnId: transaction.txnID, createdAt })
			.then(response => {
				if (response.status !== 200) throw new Error('Unexpected JPM response');
				setSubmitting(false);
				const responseMessage = response.data || 'JPM notification found.';
				setStatus(`JPM response: ${responseMessage}. Local processing continues asynchronously.`);
				setStatusTone('success');
				sessionStorage.setItem(requestKey, '1');
			})
			.catch(err => {
				setSubmitting(false);
				if (err.response && err.response.status === 404) {
					setStatus('No JPM notification was found. Please confirm the date, time, site, and transaction ID in the JPM panel before retrying.');
					setStatusTone('warning');
				} else {
					setStatus('Failed to submit the JPM notification request. Please try again.');
					setStatusTone('error');
				}
			});
	};

	return (
		<Dialog
			open={Boolean(transaction)}
			onClose={submitting ? undefined : onClose}
			fullWidth
			maxWidth="xs"
		>
			<DialogTitle>Request JPM auth code from JPM</DialogTitle>
			<DialogContent>
				<Typography className={classes.reminder}>
					Transaction {transaction.txnID} · Site {transaction.subDept}
				</Typography>
				<form id="jpm-notification-recovery-form" onSubmit={handleSubmit}>
					<TextField
						autoFocus
						fullWidth
						margin="normal"
						label="JPM date and time"
						id="jpmDateTime"
						type="datetime-local"
						inputProps={{ 'data-testid': 'jpm-recovery-date-time' }}
						value={jpmDateTime}
						onChange={e => setJpmDateTime(e.target.value)}
						InputLabelProps={{ shrink: true }}
						disabled={submitting || statusTone === 'success'}
						required
					/>
				</form>
				{status ? <Typography className={classes[statusTone]} role="status" data-testid="jpm-recovery-status">{status}</Typography> : null}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={submitting}>Close</Button>
				<Button
					color="primary"
					type="submit"
					form="jpm-notification-recovery-form"
					disabled={submitting || statusTone === 'success'}
				>
					Submit
				</Button>
			</DialogActions>
		</Dialog>
	);
}
